import React, { useEffect, useState, useCallback, useRef } from 'react';
import AdminLayout from '../../components/AdminLayout';
import api from '../../api';

const BG = '#050810';
const TIMELINE_BG = '#0a0f1e';
const TIME_LABEL = '#4b5563';
const HOUR_LABEL = '#6b7280';
const TEXT_SEC = '#6b7280';
const ACCENT = '#6366f1';

const ROW_H = 20; // 15-min row height
const TIMELINE_START_HOUR = 5;
const TIMELINE_END_HOUR = 23;
const BIZ_START = 8;
const BIZ_END = 23;
const TOTAL_SLOTS = (TIMELINE_END_HOUR - TIMELINE_START_HOUR) * 4; // 15-min slots

const STATUS_LABELS = { pending: 'Pending', confirmed: 'Confirmed', completed: 'Completed', cancelled: 'Cancelled', declined: 'Declined', no_show: 'No Show', blocked: 'Blocked' };
const STATUS_COLORS = { pending: '#f59e0b', confirmed: '#6366f1', completed: '#6b7280', cancelled: '#6b7280', declined: '#ef4444', no_show: '#ef4444', blocked: '#6b7280' };

function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

function getDaysInMonth(y, m) { return new Date(y, m + 1, 0).getDate(); }
function getFirstDayOfMonth(y, m) { return new Date(y, m, 1).getDay(); }
function dateStr(y, m, d) { return `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`; }

function isPastAppointment(a) {
  return new Date(`${a.appointment_date}T${a.end_time || a.start_time}`) < new Date();
}

function timeToMinutes(t) {
  if (!t) return null;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

function timeToOffset(t) {
  const mins = timeToMinutes(t);
  if (mins === null) return null;
  const startMins = TIMELINE_START_HOUR * 60;
  return ((mins - startMins) / 15) * ROW_H;
}

function getBlockHeight(start, end) {
  const s = timeToMinutes(start);
  const e = timeToMinutes(end);
  if (s === null || e === null) return ROW_H * 2;
  return Math.max(((e - s) / 15) * ROW_H, ROW_H);
}

function minutesToTimeStr(mins) {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// ─── Block Time Modal ──────────────────────────────────────────────────────
function BlockTimeModal({ onSave, onClose, prefillDate, prefillStart }) {
  const todayISO = new Date().toISOString().split('T')[0];
  const [date, setDate] = useState(prefillDate || todayISO);
  const [startTime, setStartTime] = useState(prefillStart || '12:00');
  const [endTime, setEndTime] = useState(() => {
    if (prefillStart) {
      const [h, m] = prefillStart.split(':').map(Number);
      const end = h * 60 + m + 60;
      return minutesToTimeStr(Math.min(end, 23 * 60));
    }
    return '13:00';
  });
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const timeSlots = [];
  for (let h = 5; h <= 23; h++) {
    for (let m = 0; m < 60; m += 15) {
      if (h === 23 && m > 0) break;
      const val = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
      const label = `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
      timeSlots.push({ val, label });
    }
  }

  const handleSave = async () => {
    if (endTime <= startTime) return;
    setSaving(true);
    try {
      await onSave({ date, start_time: startTime, end_time: endTime, reason: reason || undefined });
      onClose();
    } finally { setSaving(false); }
  };

  const inputStyle = {
    width: '100%', padding: '12px 14px', fontSize: 14,
    background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: 8, color: '#fff', outline: 'none', boxSizing: 'border-box',
    appearance: 'none', WebkitAppearance: 'none', fontFamily: "'Inter', sans-serif",
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20, touchAction: 'manipulation' }}>
      <div style={{ background: TIMELINE_BG, border: '1px solid rgba(255,255,255,0.1)', borderRadius: 20, padding: '28px 24px', width: '100%', maxWidth: 380 }}>
        <h3 style={{ fontSize: 18, fontWeight: 800, color: '#fff', margin: '0 0 20px', textAlign: 'center', fontFamily: "'Inter', sans-serif" }}>Block Time</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 24 }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: TEXT_SEC, marginBottom: 6, display: 'block', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Date</label>
            <input type="date" value={date} min={todayISO} onChange={e => setDate(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: TEXT_SEC, marginBottom: 6, display: 'block', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Start Time</label>
            <select value={startTime} onChange={e => setStartTime(e.target.value)} style={inputStyle}>
              {timeSlots.map(s => <option key={s.val} value={s.val} style={{ background: TIMELINE_BG }}>{s.label}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: TEXT_SEC, marginBottom: 6, display: 'block', textTransform: 'uppercase', letterSpacing: '0.04em' }}>End Time</label>
            <select value={endTime} onChange={e => setEndTime(e.target.value)} style={inputStyle}>
              {timeSlots.map(s => <option key={s.val} value={s.val} style={{ background: TIMELINE_BG }}>{s.label}</option>)}
            </select>
            {endTime <= startTime && <div style={{ fontSize: 12, color: '#ef4444', marginTop: 4 }}>End time must be after start time</div>}
          </div>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: TEXT_SEC, marginBottom: 6, display: 'block', textTransform: 'uppercase', letterSpacing: '0.04em' }}>Reason (optional)</label>
            <input type="text" value={reason} onChange={e => setReason(e.target.value)} placeholder="Lunch, Personal, Closed..." style={inputStyle} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: 14, borderRadius: 10, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}>Cancel</button>
          <button disabled={saving || endTime <= startTime} onClick={handleSave} style={{ flex: 1, padding: 14, borderRadius: 10, background: saving ? 'rgba(99,102,241,0.3)' : ACCENT, border: `1px solid ${ACCENT}`, color: '#fff', fontSize: 14, fontWeight: 700, cursor: saving || endTime <= startTime ? 'not-allowed' : 'pointer', opacity: saving || endTime <= startTime ? 0.5 : 1, fontFamily: "'Inter', sans-serif" }}>
            {saving ? 'Blocking...' : 'Block This Time'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Expanded Detail Panel ────────────────────────────────────────────────
function ExpandedDetails({ a, onAction, updating, onClose }) {
  const [msg, setMsg] = useState('');
  const busy = updating === a.id;
  const isPast = isPastAppointment(a);

  return (
    <div
      onClick={e => { e.stopPropagation(); onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 9998,
        background: 'rgba(0,0,0,0.7)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        touchAction: 'manipulation',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: TIMELINE_BG, borderRadius: '20px 20px 0 0',
          padding: '24px 20px 32px', width: '100%', maxWidth: 440,
          animation: 'slideUp 0.2s ease-out', fontFamily: "'Inter', sans-serif",
        }}
      >
        <div style={{ width: 40, height: 4, borderRadius: 2, background: 'rgba(255,255,255,0.15)', margin: '0 auto 18px' }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 16 }}>
          <div>
            <div style={{ fontWeight: 800, fontSize: 18, color: '#fff' }}>{a.client_name}</div>
            <div style={{ fontSize: 14, color: '#9ca3af', marginTop: 2 }}>{a.service_name}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontWeight: 800, fontSize: 20, color: ACCENT }}>${parseFloat(a.final_price || a.service_price || 0).toFixed(0)}</div>
            <span style={{
              display: 'inline-block', marginTop: 4, fontSize: 11, fontWeight: 700,
              padding: '3px 10px', borderRadius: 20,
              background: `${STATUS_COLORS[a.status]}18`, color: STATUS_COLORS[a.status],
              border: `1px solid ${STATUS_COLORS[a.status]}40`,
            }}>
              {STATUS_LABELS[a.status] || a.status}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 16, marginBottom: 14, fontSize: 14, color: '#d1d5db' }}>
          <div>
            <span style={{ color: TEXT_SEC, fontSize: 12, marginRight: 4 }}>From</span>
            <span style={{ fontWeight: 600 }}>{formatTime(a.start_time)}</span>
          </div>
          <div>
            <span style={{ color: TEXT_SEC, fontSize: 12, marginRight: 4 }}>To</span>
            <span style={{ fontWeight: 600 }}>{formatTime(a.end_time)}</span>
          </div>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
          {a.client_phone && <div style={{ fontSize: 13, color: '#9ca3af' }}>Phone: <a href={`tel:${a.client_phone}`} style={{ color: ACCENT }}>{a.client_phone}</a></div>}
          {a.notes && <div style={{ fontSize: 13, color: '#9ca3af' }}>Notes: {a.notes}</div>}
          {a.mobile_location && <div style={{ fontSize: 13, color: '#9ca3af' }}>Location: {a.mobile_location}</div>}
          {a.is_after_hours && <div style={{ fontSize: 12, color: ACCENT, fontWeight: 600 }}>After Hours</div>}
          {(a.status === 'blocked') && (a.notes || a.reason || a.block_reason) && (
            <div style={{ fontSize: 13, color: '#9ca3af' }}>Reason: {a.notes || a.reason || a.block_reason}</div>
          )}
        </div>

        {a.status === 'pending' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <input
              style={{ fontSize: 14, padding: '12px 14px', minHeight: 44, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, color: '#fff', outline: 'none', boxSizing: 'border-box', fontFamily: "'Inter', sans-serif" }}
              placeholder="Message to client..."
              value={msg}
              onChange={e => setMsg(e.target.value)}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-success" style={{ flex: 1, padding: 14, fontSize: 15, fontWeight: 700, minHeight: 48, borderRadius: 12 }} disabled={busy} onClick={() => { onAction(a.id, 'approve', msg); onClose(); }}>Approve</button>
              <button className="btn btn-danger" style={{ flex: 1, padding: 14, fontSize: 15, fontWeight: 700, minHeight: 48, borderRadius: 12 }} disabled={busy} onClick={() => { onAction(a.id, 'decline', msg); onClose(); }}>Decline</button>
            </div>
          </div>
        )}

        {a.status === 'confirmed' && (
          <div style={{ display: 'flex', gap: 8 }}>
            {isPast ? (
              <>
                <button className="btn btn-success" style={{ flex: 1, padding: 12, fontSize: 14, fontWeight: 700, minHeight: 46, borderRadius: 12 }} disabled={busy} onClick={() => { onAction(a.id, 'complete'); onClose(); }}>Mark Complete</button>
                <button className="btn btn-ghost" style={{ flex: 1, padding: 12, fontSize: 14, color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)', fontWeight: 700, minHeight: 46, borderRadius: 12 }} disabled={busy} onClick={() => { onAction(a.id, 'no_show'); onClose(); }}>No-Show</button>
              </>
            ) : (
              <button className="btn btn-danger" style={{ flex: 1, padding: 12, fontSize: 14, fontWeight: 700, minHeight: 46, borderRadius: 12 }} disabled={busy} onClick={() => { onAction(a.id, 'cancel'); onClose(); }}>Cancel</button>
            )}
          </div>
        )}

        <button
          onClick={onClose}
          style={{ width: '100%', marginTop: 12, padding: 12, borderRadius: 10, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', color: '#9ca3af', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: "'Inter', sans-serif" }}
        >
          Close
        </button>
      </div>
    </div>
  );
}

// ─── Current Time Red Line ────────────────────────────────────────────────
function CurrentTimeIndicator() {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const iv = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(iv);
  }, []);

  const mins = now.getHours() * 60 + now.getMinutes();
  const startMins = TIMELINE_START_HOUR * 60;
  const endMins = TIMELINE_END_HOUR * 60;
  if (mins < startMins || mins > endMins) return null;

  const top = ((mins - startMins) / 15) * ROW_H;
  return (
    <div style={{ position: 'absolute', top, left: 48, right: 0, zIndex: 10, pointerEvents: 'none' }}>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', flexShrink: 0 }} />
        <div style={{ flex: 1, height: 2, background: '#ef4444' }} />
      </div>
    </div>
  );
}

// ─── Month Calendar Grid ──────────────────────────────────────────────────
function MonthCalendar({ viewYear, viewMonth, selectedDay, onSelectDay, byDate, todayStr }) {
  const dayLetters = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const firstDay = getFirstDayOfMonth(viewYear, viewMonth);

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div style={{ padding: '0 4px' }}>
      {/* Day headers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 4 }}>
        {dayLetters.map((l, i) => (
          <div key={i} style={{ textAlign: 'center', fontSize: 11, fontWeight: 600, color: TIME_LABEL, padding: '4px 0', fontFamily: "'Inter', sans-serif" }}>{l}</div>
        ))}
      </div>
      {/* Day grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2 }}>
        {cells.map((d, i) => {
          if (d === null) return <div key={`e-${i}`} style={{ height: 36 }} />;
          const ds = dateStr(viewYear, viewMonth, d);
          const isToday = ds === todayStr;
          const isSelected = selectedDay === d;
          const hasAppts = !!(byDate[ds] && byDate[ds].length > 0);

          return (
            <button
              key={d}
              onClick={() => onSelectDay(d)}
              style={{
                height: 36, border: 'none', cursor: 'pointer',
                borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: isSelected ? '#1f2937' : 'transparent',
                color: isSelected ? '#fff' : isToday ? ACCENT : hasAppts ? '#d1d5db' : TIME_LABEL,
                fontWeight: isSelected || isToday ? 700 : 400,
                fontSize: 13, fontFamily: "'Inter', sans-serif",
                position: 'relative',
              }}
            >
              {d}
              {hasAppts && !isSelected && (
                <span style={{ position: 'absolute', bottom: 2, width: 4, height: 4, borderRadius: '50%', background: ACCENT }} />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Week Strip (collapsed view) ──────────────────────────────────────────
function WeekStrip({ viewYear, viewMonth, selectedDay, onSelectDay, byDate, todayStr }) {
  const dayLetters = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  const daysInMonth = getDaysInMonth(viewYear, viewMonth);
  const activeDay = selectedDay || 1;
  const dow = new Date(viewYear, viewMonth, activeDay).getDay();
  const weekStart = activeDay - dow;

  const days = [];
  for (let i = 0; i < 7; i++) {
    const d = weekStart + i;
    days.push(d >= 1 && d <= daysInMonth ? d : null);
  }

  return (
    <div style={{ display: 'flex', justifyContent: 'space-around', padding: '0 4px' }}>
      {days.map((d, i) => {
        if (d === null) return (
          <div key={`e-${i}`} style={{ width: 40, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: TIME_LABEL }}>{dayLetters[i]}</div>
            <div style={{ height: 36 }} />
          </div>
        );
        const ds = dateStr(viewYear, viewMonth, d);
        const isToday = ds === todayStr;
        const isSelected = selectedDay === d;
        const hasAppts = !!(byDate[ds] && byDate[ds].length > 0);

        return (
          <div key={d} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, width: 40 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: TIME_LABEL }}>{dayLetters[i]}</div>
            <button
              onClick={() => onSelectDay(d)}
              style={{
                width: 36, height: 36, borderRadius: '50%', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: isSelected ? '#1f2937' : 'transparent',
                color: isSelected ? '#fff' : isToday ? ACCENT : hasAppts ? '#d1d5db' : TIME_LABEL,
                fontWeight: isSelected || isToday ? 700 : 400,
                fontSize: 13, fontFamily: "'Inter', sans-serif",
                position: 'relative',
              }}
            >
              {d}
              {hasAppts && !isSelected && (
                <span style={{ position: 'absolute', bottom: 1, width: 4, height: 4, borderRadius: '50%', background: ACCENT }} />
              )}
            </button>
          </div>
        );
      })}
    </div>
  );
}

// ─── Timeline View ────────────────────────────────────────────────────────
function TimelineView({ appointments, onAction, updating, onLongPress, selectedDateStr }) {
  const [expandedAppt, setExpandedAppt] = useState(null);
  const scrollRef = useRef(null);
  const longPressTimer = useRef(null);
  const longPressTriggered = useRef(false);

  const withTime = [];
  const withoutTime = [];
  appointments.forEach(a => {
    const top = timeToOffset(a.start_time);
    if (top !== null && top >= 0) withTime.push(a);
    else withoutTime.push(a);
  });

  // Build 15-min time slots
  const timeSlots = [];
  for (let h = TIMELINE_START_HOUR; h < TIMELINE_END_HOUR; h++) {
    for (let m = 0; m < 60; m += 15) {
      timeSlots.push({ hour: h, minute: m });
    }
  }

  // Scroll to business hours or first appointment on mount
  useEffect(() => {
    if (scrollRef.current) {
      const targetMins = withTime.length > 0
        ? withTime.reduce((min, a) => { const t = timeToMinutes(a.start_time); return t < min ? t : min; }, Infinity)
        : BIZ_START * 60;
      const offset = ((targetMins - TIMELINE_START_HOUR * 60) / 15) * ROW_H;
      scrollRef.current.scrollTop = Math.max(0, offset - ROW_H * 2);
    }
  }, [withTime.length, selectedDateStr]);

  const totalHeight = TOTAL_SLOTS * ROW_H;

  const handleTouchStart = (slotMins) => {
    longPressTriggered.current = false;
    longPressTimer.current = setTimeout(() => {
      longPressTriggered.current = true;
      onLongPress(minutesToTimeStr(slotMins));
    }, 500);
  };
  const handleTouchEnd = () => { clearTimeout(longPressTimer.current); };
  const handleTouchMove = () => { clearTimeout(longPressTimer.current); };

  const isOutsideBiz = (h) => h < BIZ_START || h >= BIZ_END;

  return (
    <div>
      <div
        ref={scrollRef}
        style={{
          position: 'relative',
          height: 'calc(100vh - 200px)',
          minHeight: 400,
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
          background: TIMELINE_BG,
        }}
      >
        <div style={{ position: 'relative', height: totalHeight }}>
          {/* Time rows */}
          {timeSlots.map((slot, i) => {
            const isHour = slot.minute === 0;
            const slotMins = slot.hour * 60 + slot.minute;
            const outside = isOutsideBiz(slot.hour);
            const label = isHour
              ? `${slot.hour % 12 || 12}${slot.hour >= 12 ? 'PM' : 'AM'}`
              : `${slot.minute}`;

            return (
              <div
                key={i}
                onTouchStart={() => handleTouchStart(slotMins)}
                onTouchEnd={handleTouchEnd}
                onTouchMove={handleTouchMove}
                onMouseDown={() => handleTouchStart(slotMins)}
                onMouseUp={handleTouchEnd}
                onMouseLeave={handleTouchEnd}
                style={{
                  position: 'absolute',
                  top: i * ROW_H,
                  left: 0,
                  right: 0,
                  height: ROW_H,
                  borderTop: isHour ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(255,255,255,0.02)',
                  display: 'flex',
                  alignItems: 'flex-start',
                  background: outside
                    ? 'repeating-linear-gradient(135deg, transparent, transparent 3px, rgba(255,255,255,0.02) 3px, rgba(255,255,255,0.02) 6px)'
                    : 'transparent',
                  cursor: 'default',
                  userSelect: 'none',
                  touchAction: 'manipulation',
                }}
              >
                <div style={{
                  width: 50,
                  flexShrink: 0,
                  fontSize: isHour ? 11 : 10,
                  fontWeight: isHour ? 600 : 400,
                  color: isHour ? HOUR_LABEL : TIME_LABEL,
                  paddingTop: 1,
                  textAlign: 'right',
                  paddingRight: 8,
                  fontFamily: "'Inter', sans-serif",
                }}>
                  {label}
                </div>
              </div>
            );
          })}

          {/* Appointment blocks */}
          {withTime.map(a => {
            const top = timeToOffset(a.start_time);
            const height = getBlockHeight(a.start_time, a.end_time);
            const isBlocked = a.status === 'blocked';
            const isPending = a.status === 'pending';

            let blockBg, blockBorder;
            if (isBlocked) {
              blockBg = 'repeating-linear-gradient(135deg, rgba(107,114,128,0.1), rgba(107,114,128,0.1) 3px, rgba(107,114,128,0.2) 3px, rgba(107,114,128,0.2) 6px)';
              blockBorder = '#4b5563';
            } else if (isPending) {
              blockBg = 'rgba(245,158,11,0.15)';
              blockBorder = 'rgba(245,158,11,0.6)';
            } else {
              blockBg = 'rgba(99,102,241,0.2)';
              blockBorder = 'rgba(99,102,241,0.6)';
            }

            return (
              <div
                key={a.id}
                onClick={(e) => { e.stopPropagation(); if (!longPressTriggered.current) setExpandedAppt(a); }}
                style={{
                  position: 'absolute',
                  top,
                  left: 54,
                  right: 4,
                  height,
                  background: blockBg,
                  borderLeft: `3px solid ${blockBorder}`,
                  borderRadius: '0 6px 6px 0',
                  padding: '4px 8px',
                  cursor: 'pointer',
                  overflow: 'hidden',
                  zIndex: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                }}
              >
                {isBlocked ? (
                  <div style={{ fontWeight: 700, fontSize: 11, color: '#6b7280', lineHeight: 1.2 }}>
                    {a.notes || a.reason || a.block_reason || 'Blocked'}
                  </div>
                ) : (
                  <>
                    <div style={{ fontWeight: 700, fontSize: 11, color: '#e0e7ff', lineHeight: 1.2 }}>
                      {formatTime(a.start_time)} - {formatTime(a.end_time)}
                    </div>
                    {height > ROW_H * 1.2 && (
                      <div style={{ fontSize: 11, color: '#a5b4fc', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {a.client_name} &middot; {a.service_name}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}

          <CurrentTimeIndicator />
        </div>
      </div>

      {/* Unscheduled fallback */}
      {withoutTime.length > 0 && (
        <div style={{ padding: '12px 0' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: TEXT_SEC, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6, paddingLeft: 4 }}>Unscheduled</div>
          {withoutTime.map(a => (
            <div
              key={a.id}
              onClick={() => setExpandedAppt(a)}
              style={{
                padding: '8px 12px', background: 'rgba(99,102,241,0.08)', borderLeft: '3px solid rgba(99,102,241,0.4)',
                borderRadius: '0 6px 6px 0', marginBottom: 4, cursor: 'pointer',
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 12, color: '#e0e7ff' }}>{a.client_name}</div>
              <div style={{ fontSize: 11, color: '#a5b4fc' }}>{a.service_name}</div>
            </div>
          ))}
        </div>
      )}

      {expandedAppt && (
        <ExpandedDetails a={expandedAppt} onAction={onAction} updating={updating} onClose={() => setExpandedAppt(null)} />
      )}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────
export default function Appointments() {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDay, setSelectedDay] = useState(today.getDate());
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [blockPrefill, setBlockPrefill] = useState({});
  const [calendarExpanded, setCalendarExpanded] = useState(() => {
    try { return localStorage.getItem('booksy_cal_expanded') !== 'false'; } catch { return true; }
  });

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const toggleCalendar = () => {
    setCalendarExpanded(prev => {
      const next = !prev;
      try { localStorage.setItem('booksy_cal_expanded', String(next)); } catch {}
      return next;
    });
  };

  const load = useCallback(() => {
    setLoading(true);
    const mm = `${viewYear}-${String(viewMonth + 1).padStart(2, '0')}`;
    api.get(`/appointments?month=${mm}`)
      .catch(() => api.get('/appointments'))
      .then(r => setAppointments(r.data))
      .finally(() => setLoading(false));
  }, [viewYear, viewMonth]);

  useEffect(() => { load(); }, [load]);

  const handleAction = async (id, action, message) => {
    setUpdating(id);
    try {
      if (action === 'approve') await api.put(`/appointments/${id}/approve`, { admin_message: message || null });
      else if (action === 'decline') await api.put(`/appointments/${id}/decline`, { admin_message: message || null });
      else if (action === 'complete') await api.put(`/appointments/${id}/complete`);
      else if (action === 'no_show') await api.put(`/appointments/${id}/no-show`);
      else if (action === 'cancel') await api.put(`/appointments/${id}/cancel`);
      load();
    } finally { setUpdating(null); }
  };

  const handleBlockTime = async (data) => {
    await api.post('/appointments/block', data);
    load();
  };

  const handleLongPress = (timeStr) => {
    const prefillDate = selectedDay ? dateStr(viewYear, viewMonth, selectedDay) : new Date().toISOString().split('T')[0];
    setBlockPrefill({ date: prefillDate, start: timeStr });
    setShowBlockModal(true);
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
    else setViewMonth(m => m - 1);
    setSelectedDay(1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
    else setViewMonth(m => m + 1);
    setSelectedDay(1);
  };

  const todayStr = dateStr(today.getFullYear(), today.getMonth(), today.getDate());

  const byDate = {};
  appointments.forEach(a => {
    if (!byDate[a.appointment_date]) byDate[a.appointment_date] = [];
    byDate[a.appointment_date].push(a);
  });

  const selectedDateStr = selectedDay ? dateStr(viewYear, viewMonth, selectedDay) : null;
  const dayAppts = selectedDateStr ? (byDate[selectedDateStr] || []).sort((a, b) => (a.start_time || '').localeCompare(b.start_time || '')) : [];

  const goToday = () => {
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
    setSelectedDay(today.getDate());
  };

  return (
    <AdminLayout>
      <div style={{ fontFamily: "'Inter', sans-serif", background: BG, minHeight: '100vh', margin: '-24px -16px', padding: 0, position: 'relative' }}>

        {/* ── TOP BAR (sticky) ── */}
        <div style={{
          position: 'sticky', top: 0, zIndex: 100, background: BG,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '12px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)',
        }}>
          {/* Left: bell + Today + arrow */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
              <path d="M13.73 21a2 2 0 0 1-3.46 0" />
            </svg>
            <button onClick={goToday} style={{ background: 'none', border: 'none', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer', padding: 0, fontFamily: "'Inter', sans-serif" }}>
              Today
            </button>
            <button onClick={toggleCalendar} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 12, cursor: 'pointer', padding: '2px 4px', fontFamily: "'Inter', sans-serif" }}>
              {calendarExpanded ? '\u25B2' : '\u25BC'}
            </button>
          </div>

          {/* Center: month + year */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <button onClick={prevMonth} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 18, cursor: 'pointer', padding: '2px 6px', lineHeight: 1 }}>&lsaquo;</button>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', minWidth: 120, textAlign: 'center' }}>{monthNames[viewMonth]} {viewYear}</div>
            <button onClick={nextMonth} style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 18, cursor: 'pointer', padding: '2px 6px', lineHeight: 1 }}>&rsaquo;</button>
          </div>

          {/* Right: filter + menu */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
            </svg>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="1" /><circle cx="12" cy="5" r="1" /><circle cx="12" cy="19" r="1" />
            </svg>
          </div>
        </div>

        {/* ── Calendar (collapsible) ── */}
        <div style={{ padding: '0 12px 8px', overflow: 'hidden', transition: 'max-height 0.3s ease', maxHeight: calendarExpanded ? 300 : 70 }}>
          {calendarExpanded ? (
            <MonthCalendar
              viewYear={viewYear} viewMonth={viewMonth}
              selectedDay={selectedDay} onSelectDay={setSelectedDay}
              byDate={byDate} todayStr={todayStr}
            />
          ) : (
            <WeekStrip
              viewYear={viewYear} viewMonth={viewMonth}
              selectedDay={selectedDay} onSelectDay={setSelectedDay}
              byDate={byDate} todayStr={todayStr}
            />
          )}
        </div>

        {/* ── Timeline ── */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: 60 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
        ) : selectedDay ? (
          <TimelineView
            appointments={dayAppts}
            onAction={handleAction}
            updating={updating}
            onLongPress={handleLongPress}
            selectedDateStr={selectedDateStr}
          />
        ) : (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: TEXT_SEC, fontSize: 14 }}>
            Tap a day to view schedule
          </div>
        )}

        {/* ── FAB: + button ── */}
        <button
          onClick={() => { setBlockPrefill({}); setShowBlockModal(true); }}
          style={{
            position: 'fixed', bottom: 80, right: 20, zIndex: 90,
            width: 56, height: 56, borderRadius: '50%',
            background: '#1f2937', border: 'none',
            boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
          }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round">
            <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
          </svg>
        </button>
      </div>

      {showBlockModal && (
        <BlockTimeModal
          onSave={handleBlockTime}
          onClose={() => { setShowBlockModal(false); setBlockPrefill({}); }}
          prefillDate={blockPrefill.date}
          prefillStart={blockPrefill.start}
        />
      )}

      <style>{`
        @keyframes slideUp {
          from { opacity: 0; transform: translateY(40px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </AdminLayout>
  );
}
